import { Injectable, inject, signal } from '@angular/core';
import { LoginService } from '../../auth/login.service';
import {
  RecentPage,
  filterRecentPages,
  parseRecentPages,
  pushRecentPage,
  recentPagesStorageKey,
  shouldTrackRecentUrl,
} from './recent-pages';

/**
 * Local history of the last CRM pages visited (URL + breadcrumb trail).
 * Backed by `localStorage`, keyed per user id.
 */
@Injectable({ providedIn: 'root' })
export class RecentPagesService {
  private readonly loginService = inject(LoginService);
  private readonly entries = signal<RecentPage[]>(this.read());

  /** Newest-first recent pages. */
  readonly list = this.entries.asReadonly();

  /** Reload when the signed-in user changes (different storage key). */
  reloadForCurrentUser(): void {
    this.entries.set(this.read());
  }

  /**
   * Record / refresh a visit. Safe to call from a breadcrumb `effect`:
   * no-ops when URL is unauth, labels empty, or head entry is unchanged.
   */
  record(url: string, labels: string[]): void {
    if (!shouldTrackRecentUrl(url) || labels.length === 0) {
      return;
    }
    const next = pushRecentPage(this.entries(), {
      url,
      labels,
      visitedAt: Date.now(),
    });
    if (next === this.entries()) {
      return;
    }
    this.entries.set(next);
    this.write(next);
  }

  search(query: string, limit = 8): RecentPage[] {
    return filterRecentPages(this.entries(), query, limit);
  }

  private storageKey(): string {
    return recentPagesStorageKey(this.loginService.getUser()?.id);
  }

  private read(): RecentPage[] {
    if (typeof localStorage === 'undefined') {
      return [];
    }
    try {
      return parseRecentPages(localStorage.getItem(this.storageKey()));
    } catch {
      return [];
    }
  }

  private write(entries: RecentPage[]): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(entries));
    } catch {
      // Quota / private mode — keep in-memory only.
    }
  }
}
